import {useField} from 'formik'
import {FC, useEffect, useRef, useState} from 'react'

import {usePreferences} from '@/components/context/Preferences'
import {SearchInput} from '@/components/inputs/SearchInput'
import {filterEuCountries, getCountryCards, isIncludedInEu} from '@/content/countryCards'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {isMarketCovered, normalizeJurisdictionName} from '@/utils/countries/helpers'
import {useLanguage} from '@/utils/i18n/language'
import {calculateOfferingPrice, formatCategoryFees, getCategoryFees} from '@/utils/pricing'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {MarketsOptionCard} from '../../form/markets/selection/MarketsOptionCard'

type MarketsCheckboxListProps = {
  markets?: 'all' | 'regional'
  numOfMarkets?: number
  isSearchable?: boolean
  isSearchFocusable?: boolean
  alreadyCoveredMarkets?: Jurisdiction[]
}

export const MarketsCheckboxList: FC<MarketsCheckboxListProps> = ({
  markets = 'regional',
  numOfMarkets,
  isSearchable = false,
  isSearchFocusable = false,
  alreadyCoveredMarkets,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {currency} = usePreferences()
  const [field, helpers, {setValue}] = useField<Jurisdiction[]>('jurisdictions')
  const {modalSearchPlaceholder, alreadyCovered} = useTrademarkVerificationContent().markets
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const {origin, destination} = usePreferences()

  const categoryFees = (code: Jurisdiction) =>
    formatCategoryFees(getCategoryFees(code, currency), currency, language)

  const handleChange = (isChecked: boolean, code: Jurisdiction) => {
    setValue(
      isChecked
        ? field.value.filter((country) => country !== code)
        : [...field.value, code].includes(Jurisdiction.EU)
        ? filterEuCountries([...field.value, code])
        : [...field.value, code],
    )
  }

  useEffect(() => {
    if (isSearchFocusable) {
      setTimeout(() => inputRef?.current?.focus(), HOVER_TRANSITION)
    }
  }, [isSearchFocusable])

  const regionalCountryCards = getCountryCards(language, origin, destination)
  const allCountryCards = getCountryCards(language, null, null, 0, true)

  const normalizedQuery = normalizeJurisdictionName(query)
  const filteredCountries =
    isSearchable && query
      ? allCountryCards.filter(
          ({title}) => normalizeJurisdictionName(title).indexOf(normalizedQuery) >= 0,
        )
      : allCountryCards

  const countryCardsList = markets === 'regional' ? regionalCountryCards : filteredCountries
  const numberOfMarkets = numOfMarkets || countryCardsList.length

  return (
    <>
      {isSearchable && (
        <div className="search">
          <SearchInput
            ref={inputRef}
            query={query}
            setQuery={setQuery}
            placeholder={t(modalSearchPlaceholder)}
          />
        </div>
      )}
      <ul className="wrapper">
        {countryCardsList.map(({key, title, tooltipText, dataTest}, i) => {
          const isEuIncluded = isIncludedInEu(key, field.value)
          const isChecked = field.value.includes(key)
          const price = calculateOfferingPrice([key], currency).total

          const isAlreadyCoveredMarket = isMarketCovered(alreadyCoveredMarkets, key)

          return (
            i < numberOfMarkets && (
              <MarketsOptionCard
                key={key}
                jurisdiction={key}
                title={title}
                price={price}
                isSelected={isChecked || isAlreadyCoveredMarket}
                euIncluded={isEuIncluded}
                isDisabled={isAlreadyCoveredMarket}
                disabledText={isAlreadyCoveredMarket ? t(alreadyCovered) : ''}
                onChange={() => handleChange(isChecked, key)}
                tooltipText={t(tooltipText, {ns: 'page', ...categoryFees(key)})}
                dataTest={dataTest}
                hasError={!isAlreadyCoveredMarket && !!helpers.error && helpers.touched}
              />
            )
          )
        })}
      </ul>

      <style jsx>{`
        .wrapper {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          margin: -${theme.spacing[3]} -${theme.spacing[2]} 0 -${theme.spacing[2]};
        }

        .search {
          margin-bottom: ${theme.spacing[4]};
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            margin-left: 0;
            margin-right: 0;
          }

          .search {
            margin-bottom: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
